/**
	* Pin drop tool view
	* Handles: map click search query
	* @module PinDropTool
*/

// React & OpenLayers
import React, { useState, useEffect } from "react";
import OlMap from "ol/Map";
import Point from "ol/geom/Point";
import Icon from "ol/style/Icon";
import Feature from "ol/Feature";
import Layer from "ol/layer/Layer";
import Geometry from "ol/geom/Geometry";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { transform, transformExtent } from "ol/proj";

// jQuery
import $ from "jquery";

// Models
import Locale from "lib/interfaces/locale";
import MapMarker from "lib/classes/mapmarker";
import Vector2 from "lib/classes/vector2";
import MapToolMode from "lib/enums/maptoolmode";
import PinDropToolState from "lib/interfaces/states/pindroptool";
import OpusLayer from "lib/classes/opuslayer";
import OpusLayerGroup from "lib/classes/opuslayergroup";
import { getToolModeByName, getToolEnabled } from "lib/utilities/toolmodes";

// Tools
import { makeUrl, time, updateURLParameter, parseURLs } from "lib/utilities/misc";
import { distanceCoords, reverseNormal } from "lib/utilities/maths";
import { parseWKTFeature, removeFeatureFromSource } from 'lib/utilities/feature';
import { getAllLayerCodes, getOpusLayerByCode, getOpusLayersVisible, getAllOpusLayersVisible, getMapZoom } from "lib/utilities/map";
import { fetchJson } from "lib/utilities/network";
import { assetURL } from "lib/utilities/asset";
import { getDrawingToolEnabled } from "lib/utilities/toolmodes";

// Views
import SearchResult from 'views/SearchResult';

// Lang
import { getLocaleString } from "lib/localization/vendor";

// Popups
import SearchResultPopup from "views/popups/SearchResult";
import SearchResultsPopup from 'views/popups/PinDropSearchResults';

// Styles
import "styles/css/PinDropTool.scss";
import "styles/css/MapLayerTool.scss";

interface PinDropToolProps {
	olMap:OlMap,
	currentToolMode:MapToolMode
}

// The source url of the map marker image
const markerIconSource:string = "assets/images/marker.png";

var mapConfig:any;
var firstResultsDraw:boolean = true;
var firstInfoDraw:boolean = true;
var zoomTimer:any;
var layerGroups:OpusLayerGroup[];
var state:PinDropToolState = {
	featuresAtCursor: [],
	featuresAtCursorOnClick: [],
	mapClickTimer: null,
	clickEvent: null,
	layers: [],
	marker: null
}
var highlightFeature:Feature<Geometry>;

export default function PinDropTool({olMap, currentToolMode}:PinDropToolProps) {
	mapConfig = olMap.get("opus-config");
	layerGroups = olMap.get("opus-layer-groups");
	
	const sitesLayer:VectorLayer<VectorSource> = olMap.get("sites-layer");
	
	const [markerShowing, setMarkerShowing] = useState<boolean>(false);
	const [markerCoords, setMarkerCoords] = useState<Vector2>();
	const [shouldClick, setShouldClick] = useState<boolean>(false);
	const [searching, setSearching] = useState<boolean>(false);
	const [searchResult, setSearchResult] = useState<any>();
	const [hadSearchResult, setHadSearchResult] = useState<boolean>(false);
	const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
	const [layersVisible, setLayersVisible] = useState<OpusLayer[]>(getOpusLayersVisible(olMap));
	const [searchResultsExpanded, setSearchResultsExpanded] = useState<any[]>([]);
	const [mapSearchError, setMapSearchError] = useState<boolean>(false);
	const [_layerGroups, _setLayerGroups] = useState<OpusLayerGroup[]>([]); // Dummy state value, not used
	
	// Initialize
	useEffect(() => {
		
		_events.on('place-marker', (coordinate:Array<number>, pixel:Array<number>) => {
			mapClicked({pixel: pixel, coordinate: coordinate});
		});
		
	}, []);
	
	// Setup the map after the render and each time the map value changes
	useEffect(() => {
		setupMap();
		
		/*
			Remove the map marker from anywhere in the project
		*/
		_events.on("remove-marker", () => {
			removeMapMarker();
		});
		
		return () => {
			_events.unall("remove-marker");
		}
	}, [olMap]);
	
	// Update the layer groups state to redraw after map config reload (if lang changed)
	useEffect(() => {
		refreshHack();
	}, [olMap.get("opus-config"), _config.lang.locale]);
	
	// Clear the map click timer when the tool mode changes to avoid unexpected pins being dropped
	useEffect(() => {
		if(state.mapClickTimer) {
			clearTimeout(state.mapClickTimer);
			state.mapClickTimer = null;
		}
		if(!isToolSelected()) {
			clearSRHighlightFeature();
		}
	}, [currentToolMode]);
	
	// Call marker showing event
	useEffect(() => {
		_events.invoke("marker-showing", markerShowing);
	}, [markerShowing]);
	
	// When search result changes, redefine the event
	useEffect(() => {
		/*
			A global event that returns the search result layers array
		*/
		_events.unall("getPinDropLayers");
		_events.on("getPinDropLayers", (callback:Function) => {
			callback(state.layers, searchResult);
		});
		
		// A global event broadcasts the new search results array
		_events.invoke("pin-drop-layers", state.layers, searchResult);
		clearSRHighlightFeature();
	}, [searchResult]);
	
	/*
		* Refresh hack
		* @remarks
		* This is only necessary because rather than using react state to store opus layer groups
		* (we are using the property opus-layer-groups on olMap instead)
		* This is because we need to access these groups easily outside of this module
		* 
		* The state object _layerGroups stores the layer groups too, but is not used.
	*/
	const refreshHack = () => {
		_setLayerGroups([...layerGroups]);
	}
	
	/**
		Clear sr highlight feature
	 */
	const clearSRHighlightFeature = () => {
		highlightFeature && sitesLayer.getSource().removeFeature(highlightFeature);
		highlightFeature = null;
	}
	
	/**
		Setup the map
	*/
	const setupMap = () => {
		// Map click handler for doing searches
		olMap.on("click", (e:any) => {
			
			if(!_config.pinDropEnabled) return;
			
			// Ignore click if any drawn features are selected
			if(featuresSelected()) return;
			
			/*
				Record a snapshot of the current features at cursor so the cursor feature
				checks when mapClicked is called is accurate to when the mouse was clicked
			*/
			state.featuresAtCursorOnClick = [...state.featuresAtCursor];
			
			if(state.mapClickTimer) {
				clearTimeout(state.mapClickTimer);
				state.mapClickTimer = null;
			}else{
				state.mapClickTimer = setTimeout((e:any) => {
					/*
						Tell the component state to call the mapClicked function from 
						outside the setTimeout closure so that all values are up to date.
					*/
					state.clickEvent = e;
					setShouldClick(true);
					state.mapClickTimer = null;
				}, 300, e);
			}
		});
		
		// Add a marker feature layer to the map
		olMap.addLayer(state.marker.layer);
		
		// Map marker layer invisible by default
		state.marker.layer.setVisible(false);
		
		// Map pointer hand when over marker
		olMap.on("pointermove", (e:any) => {
			const pixel = olMap.getEventPixel(e.originalEvent);
			
			state.featuresAtCursor = [];
			
			olMap.forEachFeatureAtPixel(e.pixel, (feature:Feature<Geometry>) => {
				state.featuresAtCursor.push(feature);
			});
		});
	}
	
	/**
		* Map has been clicked, perform a server query & drop a pin
		* @param {any} e - Click event
	*/
	const mapClicked = (e:any) => {
		// Drawing tool is active, disable this feature.
		if(getDrawingToolEnabled(currentToolMode)) return;
		
		// An editable feature is at the cursor, disable this feature
		if(isEditableFeatureAtCursor(true) || isExistingSiteAtCursor(true)) return;
		
		const zoom:number = getMapZoom(olMap);
		setMapSearchError(zoom <= 11)
		
		// Pixel point of click
		const pixel:Array<number> = e.pixel;
			
		// Too much to the left
		if(pixel[0] < 50) return;
			
		// Co-ordinates of click
		const coordinate:Array<number> = e.coordinate;
		
		const size:Array<number> = olMap.getSize();
		const extents:Array<number> = olMap.getView().calculateExtent(size);
		const bbox:Array<number> = transformExtent(
			extents,
			mapConfig.epsgCodeView,
			mapConfig.epsgCode
		);
		
		const width:number = size[0];
		const height:number = size[1];
		
		const x:number = Math.floor(pixel[0]);
		const y:number = Math.floor(pixel[1]);
		
		const url:string = makeUrl(mapConfig.doubleClick.url, new Map<string, any>([
			["service", "WMS"],
			["version", "1.1.1"],
			["request", "GetFeatureInfo"],
			["layers", "none"],
			["styles", ""],
			["srs", mapConfig.epsgCode],
			["bbox", bbox.join(",")],
			["width", width],
			["height", height],
			["query_layers", getAllLayerCodes(olMap)],
			["info_format", "application/json"],
			["x", x],
			["y", y]
		]));
		
		placeMapMarker(e.coordinate, e.pixel);
		
		// Start the search!
		if(_config.queryLayersEnabled) {
			// Set the search states to searching
			setSearching(true);
			setHadSearchResult(searchResult?.length > 0);
			setSearchResult(null);
			
			//setTimeout(sendFetch, 100, url);
			sendFetch(url);
		}
	}
	const sendFetch = (url:string) => {
		fetchJson(url)
		.then((results:any[]) => {
			// Clear existing layers
			state.layers = [];
			
			results.forEach((result:any) => {
				const opusLayer:OpusLayer = getOpusLayerByCode(result.layerId, olMap);
				
				state.layers.push(opusLayer);
				
				try {
					result.feature = parseWKTFeature(result.geom, mapConfig);
				} catch(e) {
					console.warn(`Could not parse WKT for layer ${result.layerId}:`, e);
					result.feature = null;
				}
				
				try {
					// Support for new AC encoded data field
					result.customFields = JSON.parse(result.json);
				} catch(e) {};
			});
			
			// Broadcast updated layers and full result set
			_events.invoke("pin-drop-layers", state.layers, results);
			
			// Show or hide the results panel based on presence of results
			setShowSearchResults(results.length > 0);
			
			setSearchResult(results);
			setSearching(false);
		});
	}
	
	const featuresSelected = ():boolean => {
		const selectedFeatures:Feature<Geometry>[] = olMap.get("selected-features");
			
		/*
			The map has been clicked whilst features are selected.
			Allow the deselect to happen without dropping a pin.
		*/
		
		return selectedFeatures?.length > 0;
	}
	
	const mapMarkerClicked = ():boolean => {
		let clickedMarker:boolean = false;
		
		state.featuresAtCursorOnClick.forEach((feature:Feature<Geometry>) => {
			if(feature == state.marker?.feature) {
				removeMapMarker();
				clickedMarker = true;
			}
		});
		
		return clickedMarker;
	}
	
	/**
		* Place map marker on co-ordinate
		* 
		* @param {Array<number>} coordinate - x & y coordinate
	*/
	const placeMapMarker = (coordinate:Array<number>, pixel:Array<number>) => {
		state.marker.layer.setVisible(true);
		
		setMarkerShowing(true);
		
		setMarkerCoords(new Vector2(coordinate[0], coordinate[1]));
		
		// Move the marker to the co-ordinates
		const point:Point = state.marker.feature.getGeometry() as Point;
		
		point.setCoordinates(coordinate);
		state.marker.setPixel(pixel[0], pixel[1]);
		state.marker.setMousePosition(coordinate[0], coordinate[1]);
		
		focusOnMarker(true);
	}
	
	/**
		Remove map marker
	*/
	const removeMapMarker = () => {
		state.marker.layer.setVisible(false);
		setMarkerShowing(false);
		setSearchResult(null);
	}
	
	/**
		Show search results panel
	 */
	const closePanel = () => {
		//removeMapMarker(); // Opus issue 163 // Opus issue 183
		setShowSearchResults(false);
	}
	
	/**
		Expand the panel
	 */
	const expandPanel = () => {
		SearchResultsPopup.set('search-results', searchResult);
		SearchResultsPopup.set('map', olMap);
		SearchResultsPopup.set('result-clicked', focusOnGeom);
		
		_ui.showPopup(SearchResultsPopup);
	}
	
	/**
		Get the marker coords
		@returns {Array<number>} co-ordinates of the marker in WGS 84 projection
	*/
	const getMarkerCoords = () => {
		const point:Point = state.marker.feature.getGeometry() as Point;
		return transform(
			point.getCoordinates(),
			mapConfig.epsgCodeView,
			"EPSG:4326" // lat long
		);
	}
	
	/**
		Get the marker coords
		@returns {Array<number>} co-ordinates of the marker in the view's projection
	*/
	const getMarkerCoordsView = () => {
		const point:Point = state.marker.feature.getGeometry() as Point;
		return point.getCoordinates();
	}
	
	const showSearchResultBox = () => {
		if(searchResult?.length < 1) return;
		
		setShowSearchResults(!showSearchResults);
		
		// Force tool mode to select so conflicting right hand menus are hidden
		_ui.setToolMode(MapToolMode.Select);
	}
	
	/**
		Is there a search status?
	*/
	const hasSearchStatus = () => {
		return searching || mapSearchError || (searchResult && !showSearchResults); // always show when a search result exists or is searching
		//return searchResult?.length > 0 && !showSearchResults;
	}
	
	/*
		Method will focus view on the layer in question and make it visible
	*/
	const clickOpusLayer = (opusLayer:OpusLayer, visible:boolean) => {
		_events.invoke("toggle-layer", opusLayer, visible); // MapLayerTool
		
		setLayersVisible(getOpusLayersVisible(olMap));
	}
	
	/**
		Focus on the result geom
	 */
	const focusOnGeom = (result:any) => {
		// <HARDCODE>
		const skipFocus:string[] = ['o:13931'];
		if(skipFocus.includes(result.layerId)) {
			return;
		}
		// </HARDCODE>
		
		if(result.feature) {
			const extent:number[] = result.feature.getGeometry().getExtent();
			
			olMap.getView().fit(extent, { padding: [10, 10, 10, 10], duration: 500 });
			
			// Remove existing highlighted feature
			clearSRHighlightFeature();
			
			highlightFeature = result.feature;
			//highlightFeature.setId(-110);
			highlightFeature.set('highlight', true);
			
			sitesLayer.getSource().addFeature(highlightFeature);
		}
	}
	
	/*
		Method for handling info button click in map layer list
	*/
	const clickListInfoButton = (searchResult:any) => {
		//console.log(searchResult);
		
		SearchResultPopup.set("title", searchResult.title);
		SearchResultPopup.set("search-result", searchResult);
		SearchResultPopup.set("map", olMap);
		
		_ui.showPopup(SearchResultPopup);
	}
	
	/**
		* Toggle all layers
		* @param {boolean} Visible or not?
	*/
	const toggleAllLayers = (visible:boolean) => {
		for(let opusLayer of state.layers) {
			clickOpusLayer(opusLayer, visible);
		}
	}
	
	/**
		* Get if all the search res layers are visible
		* @returns {boolean}
	*/
	const getAllSearchResultsVisible = () => {
		for(let opusLayer of state.layers) {
			if(!opusLayer.getVisible())
				return false;
		}
		return true;
	}
	
	/**
		Focus map on marker
	*/
	const focusOnMarker = (noZoom:boolean = false) => {
		if(zoomTimer)
			clearTimeout(zoomTimer);
			
		if(!getToolEnabled(MapToolMode.MapControl, mapConfig))
			return;
		
		zoomTimer = setTimeout(() => {
			const coords:Array<number> = getMarkerCoordsView();
			const resolution:number = olMap.getView().getResolution();
			
			//coords[1] += -10 * resolution;
			
			const d:number = 200;
			
			if(!noZoom)
				olMap.getView().animate({center: coords, zoom: 18, duration: d});
			else
				olMap.getView().animate({center: coords, duration: d});
		}, 100);
	}
	
	/**
		Draw search state
	*/
	const drawSearchState = () => {
		if(mapSearchError) {
			return (
				<i>{getLocaleString("search-zoom-min")}</i>
			)
		}
		if(searching) {
			return (
				<i>{getLocaleString("searching")}...</i>
			)
		}
		if(searchResult?.length > 0 || searchResult) {
			return (
				<button onClick={() => showSearchResultBox()}>
					<img src={assetURL("assets/images/address.png")} className="icon" style={
						{padding: 5, width: 15}
					} /> 
					{searchResult.length} {searchResult.length == 1 ? getLocaleString("resultfound") : getLocaleString("resultsfound")}
				</button>
			)
		}
		return (<></>);
	}
	
	/**
		Draw the info panel when a marker is placed
	*/
	const drawMarkerInfoPanel = () => {
		const coords:Array<number> = getMarkerCoords();
		
		let hidden:boolean = !markerShowing;
		let className:string = `rect pin-drop-panel pin-drop-panel--hidden`;
		let x:number = coords[0];
		let y:number = coords[1];
		
		if((_dimensions.height < 400 || _dimensions.width < 840) && markerShowing && _ui.getRightPanelOpen()) {
			/*
				When the screen height is too short, just get rid of this box
				but only if the search results panel is also open
			*/
			hidden = true;
		}else
		if(_dimensions.width < 980 && markerShowing) {
			/*
				When screen width is too narrow & the opus attribution is visible, 
				put coordinate box higher up
			*/
			if(_dimensions.width < 510) {
				className += " pin-drop-panel--highest";
			}else
			if(_config.showOpusAttribution)
				className += " pin-drop-panel--higher";
		}
		
		// Sliding effect for the marker info panel
		useEffect(() => {
			if(firstInfoDraw) {
				firstInfoDraw = false;
				return;
			}
			
			if(hidden)
				$("#pin-drop-panel").slideUp(_ui.transitionUpOptions);
			else
				$("#pin-drop-panel").slideDown(_ui.transitionDownOptions);
			
		}, [hidden]);
		
		const statusClassName:string = `pin-drop-panel__search-status${hasSearchStatus() ? " pin-drop-panel__search-status--active" : ""}`;
		
		return (
			<>
				<div id="pin-drop-panel" className={className}>
					<div className="pin-drop-panel__content">
						<div className="pin-drop-panel__row" onClick={() => focusOnMarker()} style={{cursor: "pointer"}}>
							<div className="pin-drop-panel__item">
								<img src={assetURL(markerIconSource)} className="pin-drop-panel__icon" />
							</div>
							<div className="pin-drop-panel__item">
								<div className="pin-drop-panel__coords">
									{y.toFixed(6)} , {x.toFixed(6)}
								</div>
							</div>
						</div>
						<div className="pin-drop-panel__row">
							<div className={statusClassName}>
								{hasSearchStatus() && drawSearchState()}
							</div>
						</div>
					</div>
				</div>
			</>
		)
	}
	
	/**
		Draw toggle button for all layer groups
	*/
	const drawAllToggleButton = () => {
		const allChecked:boolean = getAllSearchResultsVisible();
		const eyeImage:string = `assets/images/eye${!allChecked ? "hide" : ""}.png`;
		const className:string = `toggle-btn ${allChecked ? ` toggle-btn--selected ${_ui.getSelClassName()}` : ""}`;
		
		return (
			<div className={className} onClick={() => toggleAllLayers(!allChecked)} data-tip={getLocaleString("toggleall")} data-delay-show="700">
				<img src={assetURL(eyeImage)} />
			</div>
		)
	}
	
	/**
		Draw expand button
	 */
	const drawExpandButton = () => {
		if(state.layers.length < 1)
			return null;
			
		return (
			<div className="layer-group__ib toggle-btn layer-row__control" onClick={() => expandPanel()}>
				<img src={assetURL('assets/images/expand.png')} /> 
			</div>
		)
	}
	
	/**
		Draw close panel button
	 */
	const drawCloseButton = () => (
		<div className={`toggle-btn toggle-btn--selected ${_ui.getSelClassName()}`} onClick={() => closePanel()}>
			<img src={assetURL("assets/images/cross.png")} />
		</div>
	);
	
	/**
		Draw search results here
	*/
	const drawSearchResults = () => {
		const results:any[] = [];
		
		for(let i in searchResult) {
			const result:any = searchResult[i];
			const isExpanded:boolean = searchResultsExpanded.includes(result);
			const opusLayer:OpusLayer = getOpusLayerByCode(result.layerId, olMap);
			const checked:boolean = opusLayer.getVisible();
			const className:string = `layer-group__layer ${checked ? ` layer-group__layer--selected ${_ui.getSelClassName()}` : ""}`;
			const eyeImage:string = `assets/images/eye${!checked ? "hide" : ""}.png`;
			const collapseImage:string = `assets/images/slide${isExpanded ? "up" : "down"}.png`;
			const minScreenWidth:number = 500;
			
			/* Image URL */
				let url:string = result.image;
				
				const size:number = 40;
				
				url = updateURLParameter(url, "width", size.toString());
				url = updateURLParameter(url, "height", size.toString());
			
			const clickVisibility = (e:any) => {
				clickOpusLayer(opusLayer, !checked);
				
				e.stopPropagation();
			}
			const clickInfoButton = (e:any) => {
				clickListInfoButton(result);
				
				e.stopPropagation();
			}
			const toggleCollapse = (e:any) => {
				const expanded:any[] = [];
				
				if(isExpanded) {
					expanded.splice(expanded.indexOf(result), 1);
				}else{
					expanded.push(result);
					focusOnGeom(result);
				}
				
				setSearchResultsExpanded(expanded);
				e.stopPropagation();
			}
			const drawQuickResultButton = () => {
				const urls:any[] = parseURLs(result.urls);
				let quickUrl:string = '';
				
				urls.forEach(url => {
					if(url.label == mapConfig.quickResultLinkLabel)
						quickUrl = url.url;
				});
				
				if(!quickUrl)
					return null;
				
				const clickUrlButton = (e:any) => {
					e.stopPropagation();
					
					window.open(quickUrl, '_blank');
				}
				
				return (
					<div className="layer-group__ib detail-btn layer-row__control" onClick={clickUrlButton}>
						<div style={{color: 'black', fontSize: 13}}>
							{mapConfig.quickResultLinkLabel}
						</div>
					</div>
				)
			}
			
			results.push(
				<div className={className} key={i} onClick={toggleCollapse}>
					<div className="layer-row__meta">
						<div className="layer-row__image">
							<img src={url} /> 
						</div>
						<div className="layer-row__title" title={getLocaleString("inforesult")}>
							{result.title || getLocaleString("notitle")}
							<div className="layer-row__subtitle">
								{opusLayer.name}
							</div>
						</div>
						<div className="layer-row__controls">
							{
								!opusLayer.isHidden ? (
									<div className="layer-group__ib toggle-btn layer-row__control" onClick={clickVisibility}>
										<img src={assetURL(eyeImage)} /> 
									</div>
								) : null
							}
							<div className="layer-group__ib toggle-btn layer-row__control" onClick={clickInfoButton}>
								<img src={assetURL('assets/images/expand.png')} /> 
							</div>
							<div 
								className={
									`toggle-btn ${isExpanded ? ` toggle-btn--selected ${_ui.getSelClassName()}` : ""}`
								} 
								onClick={toggleCollapse} 
								data-tip={getLocaleString(isExpanded ? "uncollapse" : "collapse")} 
								data-delay-show="400"
							>
								<img src={assetURL(collapseImage)} /> 
							</div>
							
						</div>
					</div>
					
					{mapConfig.quickResultLinkLabel && drawQuickResultButton()}
					
					<div className={`pin-drop-panel__sres-content-container ${isExpanded ? 'pin-drop-panel__sres-content-container--expanded' : ''}`}>
						{
							isExpanded && 
							(
								<div 
									className="pin-drop-panel__sres-collapsable" 
									onClick={e => e.stopPropagation()}
								>
									<div 
										className="pin-drop-panel__sres-collapsable__content"
									>
										<SearchResult 
											olMap={olMap} 
											result={result} 
											title={result.title} 
										/>
									</div>
								</div>
							)
						}
					</div>
				</div>
			)
		}
		
		return results;
	}
	
	/**
		Draw the search results panel
	*/
	const drawSearchResultsPanel = () => {
		let hidden:boolean = !showSearchResults || !searchResult || !markerShowing || state.currentToolMode != MapToolMode.Select;
		
		// Register the right panel displaying status with the UI manager
		_ui.registerRightPanel(!hidden, "layer-results-rect");
			
		const coords:Array<number> = getMarkerCoords();
		const className:string = `rect rect__toolbox--right rect__toolbox--hidden rect__toolbox--expanding rect__toolbox--under`;
		
		let x:number = coords[0];
		let y:number = coords[1];
		
		// Sliding effect for the search results panel
		useEffect(() => {
			if(firstResultsDraw) {
				firstResultsDraw = false;
				return;
			}
			
			if(hidden)
				$("#layer-results-rect").slideUp(_ui.transitionUpOptions);
			else
				$("#layer-results-rect").delay(200).slideDown(_ui.transitionDownOptionsFlex);
		}, [hidden]);
		
		return (
			<div id="layer-results-rect" className={className}>
				<div className="rect__toolbox__heading pin-drop-panel__sres-heading">
					{getLocaleString("searchresults")}
					
					<div className="pin-drop-panel__rescoords" onClick={() => focusOnMarker()}>
						<img src={assetURL(markerIconSource)} className="pin-drop-panel__icon--results" /> 
						<div>
							{y.toFixed(6)} , {x.toFixed(6)}
						</div>
					</div>
					
					<div className="rect__toolbox__right">
						{drawExpandButton()}
						{drawAllToggleButton()}
						{drawCloseButton()}
					</div>
				</div>
				
				<div className="rect__content">
					<div className="gray-label">
						{getLocaleString("sresults", [searchResult?.length || 0])}
					</div>
					{searchResult?.length > 0 && <hr />}
					{drawSearchResults()}
				</div>
			</div>
		)
	}
	
	/*
		Initiated in setupMap()
	*/
	if(shouldClick) {
		if(!featuresSelected() && !mapMarkerClicked())
			mapClicked(state.clickEvent);
			
		setShouldClick(false);
	}
	
	updateCurrentToolMode(currentToolMode);
	
	if(!state.marker) {
		// Setup marker when environment is initialised
		
		state.marker = new MapMarker(new Icon({
			src: assetURL(markerIconSource),
			crossOrigin: _config.crossOrigin,
			scale: 0.65,
			displacement: [0, 10]
		}));
	}
	
	return (
		<>
			{drawSearchResultsPanel()}
			{drawMarkerInfoPanel()}
		</>
	)
}
function updateCurrentToolMode(currentToolMode:MapToolMode) {
	state.currentToolMode = currentToolMode;
}
function isToolSelected():boolean {
	return state.currentToolMode == MapToolMode.PinDrop;
}

/**
	* Is map marker at cursor
	* 
	* @param {boolean} checkSnapshot - Use the snapshot of the feature array taken before the mapClicked timeout?
	* 
	* @returns
	* boolean true if cursor is over the map marker, false if not
*/
function isMarkerAtCursor(checkSnapshot:boolean = false):boolean {
	const arr:Feature<Geometry>[] = !checkSnapshot ? state.featuresAtCursor : state.featuresAtCursorOnClick;
		
	for(let feature of state.featuresAtCursor) {
		if(feature == state.marker.feature) 
			return true;
	}
	return false;
}

/**
	* Is feature at cursor
	* 
	* @param {boolean} checkSnapshot - Use the snapshot of the feature array taken before the mapClicked timeout?
	* 
	* @returns
	* boolean true if cursor is over any feature apart from the map marker, false if not
*/
function isFeatureAtCursor(checkSnapshot:boolean = false):boolean {
	const arr:Feature<Geometry>[] = !checkSnapshot ? state.featuresAtCursor : state.featuresAtCursorOnClick;
		
	for(let feature of arr) {
		if(feature != state.marker.feature) 
			return true;
	}
	return false;
}

/**
	* Is editable feature at cursor
	* 
	* @param {boolean} checkSnapshot - Use the snapshot of the feature array taken before the mapClicked timeout?
	* 
	* @returns
	* boolean true if cursor is over an editable feature, false if not
*/
function isEditableFeatureAtCursor(checkSnapshot:boolean = false):boolean {
	const arr:Feature<Geometry>[] = !checkSnapshot ? state.featuresAtCursor : state.featuresAtCursorOnClick;
		
	for(let feature of arr) {
		if(!feature.get("editable")) continue;
		
		return true;
	}
	return false;
}

/**
	* Is existing site at cursor
	* 
	* @param {boolean} checkSnapshot - Use the snapshot of the feature array taken before the mapClicked timeout?
	* 
	* @returns
	* boolean true if cursor is over an existing site, false if not
*/
function isExistingSiteAtCursor(checkSnapshot:boolean = false):boolean {
	const arr:Feature<Geometry>[] = !checkSnapshot ? state.featuresAtCursor : state.featuresAtCursorOnClick;
	
	for(let feature of arr) {
		if(!feature.get("existing-site") && !feature.get("highlight")) continue;
		
		return true;
	}
	return false;
}